// src/pages/ForgotPassword.tsx
import React, { useState } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
} from '@mui/material';
import { Telegram, ArrowBack } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import api from "../api/api"; // ← правильный импорт


const steps = ['Восстановление', 'Код и новый пароль'];

const ForgotPassword: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const res = await api.get('/users/forget_password', {
        params: { username_or_email: usernameOrEmail },
      });

      if (res.data.status === 'Success') {
        setSuccess('Код отправлен в @tg_typhon_bot');
        setActiveStep(1);
      } else {
        setError(res.data.detail);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка сервера');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await api.post('/users/new_password', {
        code: code.trim(),
        new_password: newPassword,
      });

      if (res.data.status === 'Success') {
        setSuccess('Пароль успешно изменён! Сейчас перенаправим на вход...');
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError(res.data.detail);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Неверный код или истёк срок действия');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 5, borderRadius: 3, boxShadow: 3 }}>
        <Typography variant="h4" sx={{ textAlign: 'center', fontWeight: 800, color: '#1E3A8A', mb: 4 }}>
          Восстановление пароля
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

        {/* Шаг 1 */}
        {activeStep === 0 && (
          <Box component="form" onSubmit={handleSendCode} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Логин или Email"
              value={usernameOrEmail}
              onChange={(e) => setUsernameOrEmail(e.target.value)}
              fullWidth
              required
            />

            <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#F0F9FF', borderRadius: 2, border: '1px solid #BAE6FD' }}>
              <Telegram sx={{ fontSize: 40, color: '#229ED9', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Код придёт в личку бота
              </Typography>
              <Button
                href="https://t.me/tg_typhon_bot"
                target="_blank"
                variant="text"
                sx={{ mt: 1, color: '#229ED9', fontWeight: 600 }}
              >
                @tg_typhon_bot → открыть
              </Button>
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || !usernameOrEmail.trim()}
              sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' }, py: 1.8 }}
            >
              {loading ? <CircularProgress size={28} color="inherit" /> : 'Отправить код'}
            </Button>
          </Box>
        )}

        {/* Шаг 2 */}
        {activeStep === 1 && (
          <Box component="form" onSubmit={handleChangePassword} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
                label="Код из бота"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                fullWidth
                required
                placeholder="Вставьте код целиком"
                inputProps={{maxLength: 32}}
                helperText="Код из сообщения в @tg_typhon_bot"
            />
            <TextField
              label="Новый пароль"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              required
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || code.length !== 8}
              sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' }, py: 1.8 }}
            >
              {loading ? <CircularProgress size={28} color="inherit" /> : 'Изменить пароль'}
            </Button>

            <Button startIcon={<ArrowBack />} onClick={() => setActiveStep(0)} variant="outlined" fullWidth>
              Назад
            </Button>
          </Box>
        )}

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button component={Link} to="/login" variant="text">
            ← Вернуться ко входу
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ForgotPassword;